import { VideoTrackingData } from '@msc-b2c/interfaces/types';
import { breakpoints } from '@msc-b2c/shared-utils';

// Utility function to push video events to adobeDataLayer
const pushToAdobeDataLayer = (eventData: VideoTrackingData): void => {
  if (window.adobeDataLayer) {
    window.didomiOnReady = window.didomiOnReady || [];
    console.log('Didomi Wrapper pushToAdobeDataLayer - About to push callback');
    window.didomiOnReady.push(() => {
      console.log('Didomi Wrapper pushToAdobeDataLayer called - Didomi is ready');
      console.log('didomiVendorsEnabled =', window.didomiState?.didomiVendorsEnabled);
      console.log('Data to be pushed:', eventData);
      window.adobeDataLayer?.push(eventData);
      console.log('Custom event video tracking pushed into the data layer');
    });
  } else {
    console.warn('adobeDataLayer is not defined');
  }
};

const predefinedPercentages = [0, 10, 25, 50, 75, 90, 100];

const calculateVideoPercent = (currentTime: number, duration: number): number => {
  const percentage = (currentTime / duration) * 100;

  // Find the closest value in the predefinedPercentages array
  return predefinedPercentages.reduce((prev, curr) => {
    return Math.abs(curr - percentage) < Math.abs(prev - percentage) ? curr : prev;
  });
};

const isAutoplay = (videoElement: HTMLVideoElement): boolean => {
  if (videoElement.closest('.cmp-parallax')) {
    return true;
  }

  const isMobile = window.innerWidth <= breakpoints.lg;
  if (isMobile && videoElement.dataset['autoplayMobile'] === 'true') {
    return true;
  }
  if (!isMobile && videoElement.dataset['autoplayDesktop'] === 'true') {
    return true;
  }

  return videoElement.hasAttribute('autoplay');
};

function isTrackedVideo(el: EventTarget | null): el is HTMLVideoElement {
  if (!(el instanceof HTMLVideoElement)) return false;
  return el.classList.contains('cmp-media__video') || el.closest('.cmp-parallax') !== null;
}

// Helper function to generate tracking data
const generateVideoTrackingData = (
  event: string,
  videoElement: HTMLVideoElement
): VideoTrackingData => ({
  event,
  video_current_time: videoElement.currentTime,
  video_duration: videoElement.duration,
  video_percent: String(calculateVideoPercent(videoElement.currentTime, videoElement.duration)),
  video_provider: 'b2c',
  video_url: videoElement.currentSrc,
  visible: true,
  autoplay: isAutoplay(videoElement),
});

const progressTrackedMap = new WeakMap<HTMLVideoElement, number[]>();

let initialized = false;

// Uses capture-phase listeners on document so that play/pause/ended/timeupdate
// events are caught regardless of when video elements appear in the DOM or
// when .play() is called. Media events don't bubble, but they DO propagate
// through the capture phase.
export function initializeVideoTracking(): void {
  if (initialized) return;
  initialized = true;

  document.addEventListener(
    'play',
    (e) => {
      if (!isTrackedVideo(e.target)) return;
      pushToAdobeDataLayer(generateVideoTrackingData('video_start', e.target));
    },
    true
  );

  document.addEventListener(
    'pause',
    (e) => {
      if (!isTrackedVideo(e.target)) return;
      const video = e.target;
      if (calculateVideoPercent(video.currentTime, video.duration) <= 90) {
        pushToAdobeDataLayer(generateVideoTrackingData('video_pause', video));
      }
    },
    true
  );

  document.addEventListener(
    'ended',
    (e) => {
      if (!isTrackedVideo(e.target)) return;
      pushToAdobeDataLayer(generateVideoTrackingData('video_complete', e.target));
    },
    true
  );

  document.addEventListener(
    'timeupdate',
    (e) => {
      if (!isTrackedVideo(e.target)) return;
      const video = e.target;

      if (!progressTrackedMap.has(video)) {
        progressTrackedMap.set(video, []);
      }
      const tracked = progressTrackedMap.get(video);
      const videoPercent = calculateVideoPercent(video.currentTime, video.duration);

      if (
        [10, 25, 50, 75, 90].includes(videoPercent) &&
        tracked &&
        !tracked.includes(videoPercent)
      ) {
        pushToAdobeDataLayer(generateVideoTrackingData('video_progress', video));
        tracked.push(videoPercent);
      }
    },
    true
  );
}

initializeVideoTracking();
